import { useState, useEffect, useRef } from "react";
import {
  Clock,
  Trophy,
  Coins,
  X,
  Check,
  ArrowLeft,
  RefreshCw,
  Target,
  Zap,
  BookOpen,
} from "lucide-react";

import { useNavigate } from "react-router-dom";
import "../styles/vocabMatchingGame.css";
import LoadingOverlay from "../components/LoadingOverlay";
import "../styles/wordRelationsGame.css";
import { API_BASE } from "../config";
import gameConfig from "../data/gameConfig.json";
import apiFetch from "../apiFetch";
import { reportHomeworkCompletion } from "../utils/homeworkCompletion";
import { reportDailyTaskCompletion } from "../utils/dailyTaskCompletion";
import { navigateReturn } from "../utils/navigation";
import { YEAR_OPTIONS, DISPLAY_YEAR_OPTIONS, mapToNearestTier, filterWordsByTier, tierLabel } from "../data/vocabYearGroups";

const CFG = gameConfig.vocabMatching;

const VocabularyMatchingGame = () => {
  const navigate = useNavigate();
  const returnTo = useRef(sessionStorage.getItem("returnTo") || "/games/vocabulary");
  const goBack = () => { sessionStorage.removeItem("returnTo"); navigate(returnTo.current); };
  const [gameState, setGameState] = useState("loading");
  const [vocabulary, setVocabulary] = useState([]);
  const [userElo, setUserElo] = useState(null);
  const [currentWords, setCurrentWords] = useState([]);
  const [currentDefinitions, setCurrentDefinitions] = useState([]);
  const [matches, setMatches] = useState(new Map());
  const [selectedWord, setSelectedWord] = useState(null);
  const [selectedDefinition, setSelectedDefinition] = useState(null);
  const [correctMatches, setCorrectMatches] = useState(0);
  const [incorrectMatches, setIncorrectMatches] = useState(0);
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [startTime, setStartTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [connections, setConnections] = useState([]);
  const [feedbackMessage, setFeedbackMessage] = useState("");
  const [coinsEarned, setCoinsEarned] = useState(0);
  const [sessionEloChanges, setSessionEloChanges] = useState([]);
  const [hwAssignmentId, setHwAssignmentId] = useState(null);
  const shownThisSession = useRef(new Set());
  const [selectedTier, setSelectedTier] = useState(() =>
    mapToNearestTier(localStorage.getItem("difficulty_tier"))
  );

  /* ── Cache homework assignmentId on mount (so Play Again sessions can still report) ── */
  useEffect(() => {
    const raw = sessionStorage.getItem("hw_params");
    if (raw) {
      try { const p = JSON.parse(raw); if (p?.assignmentId) setHwAssignmentId(p.assignmentId); } catch {}
    }
  }, []);

  useEffect(() => {
    loadVocabulary();
  }, []);

  useEffect(() => {
    if (vocabulary.length > 0) {
      generateNewRound();
      setGameState("playing");
    }
  }, [vocabulary]);

  const loadVocabulary = async () => {
    setLoading(true);
    try {
      const userId = parseInt(localStorage.getItem("userId"), 10);
      if (!userId) throw new Error("User ID not found");

      // First fetch ELO
      const eloRes = await apiFetch(
        `/api/vocab/current-elo?userId=${userId}`,
      );
      if (!eloRes.ok) throw new Error(`HTTP error! status: ${eloRes.status}`);
      const eloData = await eloRes.json();
      const fetchedElo = eloData.elo ?? CFG.defaultElo;
      setUserElo(fetchedElo);

      // Then fetch vocab
      const response = await apiFetch(`/api/vocabulary`);
      if (!response.ok) throw new Error(`Vocabulary fetch failed: ${response.status}`);
      const data = await response.json();
      setVocabulary(data.words || []);
      setStartTime(Date.now());
    } catch (err) {
      console.error("❌ Error loading vocabulary:", err);
      setError("Failed to load vocabulary data");
    } finally {
      setLoading(false);
    }
  };

  const generateNewRound = (overrideTier = null) => {
    if (vocabulary.length === 0) return;

    const tier = overrideTier !== null ? overrideTier : selectedTier;
    const allTierWords = filterWordsByTier(vocabulary, tier).filter(
      (word) => word.definition && word.definition.trim() !== ""
    );

    let available = allTierWords.filter((w) => !shownThisSession.current.has(w.word));

    // Fewer than 4 unshown words — full cycle complete, reset and start again
    if (available.length < 4) {
      shownThisSession.current.clear();
      available = [...allTierWords];
    }

    const selectedWords = [];
    const pool = [...available];
    for (let i = 0; i < 4; i++) {
      if (pool.length > 0) {
        const index = Math.floor(Math.random() * pool.length);
        selectedWords.push({ ...pool[index] });
        pool.splice(index, 1);
      }
    }

    selectedWords.forEach((w) => shownThisSession.current.add(w.word));


    const shuffledDefinitions = [...selectedWords].sort(
      () => Math.random() - 0.5,
    );

    setCurrentWords(selectedWords);
    setCurrentDefinitions(shuffledDefinitions);
    setMatches(new Map());
    setSelectedWord(null);
    setSelectedDefinition(null);
    setConnections([]);
    setFeedbackMessage("");
  };

  const handleWordClick = (word) => {
    if (selectedWord?.id === word.id) {
      setSelectedWord(null);
    } else {
      setSelectedWord(word);
      if (selectedDefinition) {
        attemptMatch(word, selectedDefinition);
      }
    }
  };

  const handleDefinitionClick = (definition) => {
    if (selectedDefinition?.id === definition.id) {
      setSelectedDefinition(null);
    } else {
      setSelectedDefinition(definition);
      if (selectedWord) {
        attemptMatch(selectedWord, definition);
      }
    }
  };

  const attemptMatch = (word, definition) => {
    const isCorrect = word.id === definition.id;

    // Update match results
    if (isCorrect) {
      setMatches((prev) => new Map(prev).set(word.id, definition.id));
      setCorrectMatches((prev) => prev + 1);
      setWordsCompleted((prev) => prev + 1);
      setFeedbackMessage("✅ Correct!");
    } else {
      setIncorrectMatches((prev) => prev + 1);
      setFeedbackMessage("❌ Incorrect, try again!");
    }

    const eloChange = isCorrect ? CFG.eloCorrect : -CFG.eloPenalty;

    const updatedElo = Math.max(0, userElo + eloChange);


    setUserElo(updatedElo);
    setSessionEloChanges((prev) => [
      ...prev,
      { word: word.word, change: eloChange },
    ]);

    // Draw connection line
    setConnections((prev) => [
      ...prev,
      { wordId: word.id, definitionId: definition.id, correct: isCorrect },
    ]);

    // Clear incorrect line after 1s
    if (!isCorrect) {
      setTimeout(() => {
        setConnections((prev) =>
          prev.filter(
            (conn) =>
              !(
                conn.wordId === word.id &&
                conn.definitionId === definition.id &&
                !conn.correct
              ),
          ),
        );
      }, 1000);
    }

    // Reset selections and feedback
    setSelectedWord(null);
    setSelectedDefinition(null);
    setTimeout(() => setFeedbackMessage(""), 2000);

    // If all matches completed, start next round
    if (isCorrect && matches.size + 1 === currentWords.length) {
      setTimeout(() => {
        generateNewRound();
      }, 1500);
    }
  };

  const addCoinsAndXPToUser = async (coins) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId || coins <= 0) return;

    try {
      // 1) Update coins
      const response = await apiFetch(
        `/api/resources/update-coins`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, delta: coins }),
        },
      );

      if (!response.ok) {
        console.error("❌ Failed to update coins:", await response.text());
        return;
      }

      // 2) Update XP (coins × 5)
      const xpResponse = await apiFetch(
        `/api/resources/xp/add`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ userId, delta: coins * CFG.xpMultiplier }),
        },
      );

      if (!xpResponse.ok) {
        console.error("❌ Failed to update XP:", await xpResponse.text());
      }
    } catch (error) {
      console.error("❌ Error updating coins/XP:", error);
    }
  };


  const saveVocabEloToDB = async (sessionCorrect) => {
    const userId = parseInt(localStorage.getItem("userId"), 10);
    if (!userId) return;

    try {
      const res = await apiFetch(`/api/vocab/save-elo`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          userId,
          finalElo: userElo,
          tier: selectedTier,
        }),
      });

      if (!res.ok) {
        console.error("Failed to save vocab ELO:", await res.text());
      } else {
        await reportHomeworkCompletion(null, "game", sessionCorrect);
        reportDailyTaskCompletion(sessionCorrect);
      }
    } catch (err) {
      console.error("❌ Error saving vocab ELO:", err);
    }
  };

  //=========================

  const finishSession = () => {
    // Nothing was attempted — don't report completion, just exit
    if (correctMatches + incorrectMatches === 0) {
      if (returnTo.current === "/daily-tasks") {
        navigate(returnTo.current);
      } else {
        setGameState("finished");
      }
      return;
    }

    // Replenish hw_params if a previous Play-Again session already consumed it
    if (hwAssignmentId && !sessionStorage.getItem("hw_params")) {
      sessionStorage.setItem("hw_params", JSON.stringify({ assignmentId: hwAssignmentId }));
    }

    // Fire-and-forget: persist words played this session for cross-session unplayed-first ordering
    const userId = parseInt(localStorage.getItem("userId"), 10);
    const sessionAnswers = sessionEloChanges.map((e) => ({ wordId: e.word, correct: e.change > 0 }));
    if (sessionAnswers.length > 0) {
      apiFetch("/api/vocab-test/save-session", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId, testType: "matching", wordCount: sessionAnswers.length, score: correctMatches, maxScore: correctMatches + incorrectMatches, answers: sessionAnswers, tier: selectedTier }),
      }).catch(() => {});
    }

    const coinMultiplier = Math.max(CFG.coinsEloMin, userElo / CFG.coinsEloScale);
    const totalCoins = Math.floor(correctMatches * coinMultiplier);
    setCoinsEarned(totalCoins);
    addCoinsAndXPToUser(totalCoins);
    saveVocabEloToDB(correctMatches);
    if (returnTo.current === "/daily-tasks") {
      sessionStorage.removeItem("returnTo");
      navigate(returnTo.current);
    } else {
      setGameState("finished");
    }
  };

  const formatTime = (ms) => {
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const rem = seconds % 60;
    return `${minutes}:${rem.toString().padStart(2, "0")}`;
  };

  const resetGame = () => {
    shownThisSession.current.clear();
    setGameState("loading");
    setCorrectMatches(0);
    setIncorrectMatches(0);
    setWordsCompleted(0);
    setCoinsEarned(0);
    setSessionEloChanges([]);
    setStartTime(Date.now());
    generateNewRound();
    setGameState("playing");
  };

  // Loading screen
  if (userElo === null || gameState === "loading") return <LoadingOverlay message="Loading vocabulary challenge…" />;

  // Results screen
  if (gameState === "finished") {
    const timeElapsed = Date.now() - startTime;

    return (
      <div className="vocabulary-results-screen">
        <div className="vocabulary-results-content">
          <Trophy className="vocabulary-trophy-icon" />
          <h2>Vocabulary Session Complete!</h2>

          <div className="vocabulary-final-stats">
            <div className="vocabulary-stat-item">
              <Target className="vocabulary-stat-icon" />
              <div>
                <div className="vocabulary-stat-label">Words Completed</div>
                <div className="vocabulary-stat-value">{wordsCompleted}</div>
              </div>
            </div>

            <div className="vocabulary-stat-item">
              <Coins className="vocabulary-stat-icon" />
              <div>
                <div className="vocabulary-stat-label">Coins Earned</div>
                <div className="vocabulary-stat-value">{coinsEarned}</div>
              </div>
            </div>

            <div className="vocabulary-stat-item">
              <Clock className="vocabulary-stat-icon" />
              <div>
                <div className="vocabulary-stat-label">Time Elapsed</div>
                <div className="vocabulary-stat-value">
                  {formatTime(timeElapsed)}
                </div>
              </div>
            </div>
          </div>

          <div className="vocabulary-session-summary">
            <h3>Session Summary</h3>
            <div className="vocabulary-accuracy-stats">
              <div className="vocabulary-accuracy-item correct">
                <Check className="vocabulary-accuracy-icon" />
                <span>Correct: {correctMatches}</span>
              </div>
              <div className="vocabulary-accuracy-item incorrect">
                <X className="vocabulary-accuracy-icon" />
                <span>Incorrect: {incorrectMatches}</span>
              </div>
            </div>

            <div className="vocabulary-elo-summary">
              <div className="vocabulary-elo-display">
                <Zap className="vocabulary-elo-icon" />
                <span>Final ELO: {userElo}</span>
              </div>

              <div className="vocabulary-elo-changes">
                <h4>ELO Changes This Session:</h4>
                <div className="vocabulary-elo-list">
                  {sessionEloChanges.slice(-10).map((change, index) => (
                    <div
                      key={index}
                      className={`vocabulary-elo-change ${change.change > 0 ? "positive" : "negative"}`}
                    >
                      <span className="vocabulary-change-word">
                        {change.word}
                      </span>
                      <span className="vocabulary-change-amount">
                        {change.change > 0 ? "+" : ""}
                        {change.change}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div className="vocabulary-action-buttons">
            <button className="vocabulary-primary-button" onClick={resetGame}>
              <RefreshCw /> New Session
            </button>
            <button
              className="vocabulary-secondary-button"
              onClick={goBack}
            >
              <ArrowLeft /> Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Main game screen
  return (
    <div className="vocabulary-game-container">
      <div className="wr-header">
        <div className="wr-header-left">
          <h1>Vocabulary Matching</h1>
          <div className="wr-elo-badge">
            <Zap size={14} /> {userElo}
          </div>
        </div>
        <div className="wr-header-right">
          <div className="wr-score">
            <span className="wr-score-correct"><Check size={14} /> {correctMatches}</span>
            <span className="wr-score-wrong"><X size={14} /> {incorrectMatches}</span>
          </div>
        </div>
      </div>

      <div className="wr-year-bar">
        <label className="wr-year-label">Year group:</label>
        <select
          className="wr-year-select"
          value={selectedTier}
          onChange={(e) => {
            const newTier = parseInt(e.target.value, 10);
            shownThisSession.current.clear();
            setSelectedTier(newTier);
            generateNewRound(newTier);
          }}
        >
          {DISPLAY_YEAR_OPTIONS.map((opt) => (
            <option key={opt.tier} value={opt.tier}>{opt.label}</option>
          ))}
        </select>
      </div>

      <div className="vocabulary-game-board">
        <div className="vocabulary-words-column">
          <h3>Words</h3>
          <div className="vocabulary-words-list">
            {currentWords.map((word) => (
              <button
                key={word.id}
                data-word-id={word.id}
                className={`vocabulary-word-item ${selectedWord?.id === word.id ? "selected" : ""} ${matches.has(word.id) ? "matched" : ""}`}
                onClick={() => handleWordClick(word)}
                disabled={matches.has(word.id)}
              >
                <span className="vocabulary-word-text">{word.word}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="vocabulary-connection-area">
          <svg className="vocabulary-connection-svg" width="100%" height="100%">
            {connections.map((connection, index) => {
              const wordElement = document.querySelector(
                `[data-word-id="${connection.wordId}"]`,
              );
              const definitionElement = document.querySelector(
                `[data-definition-id="${connection.definitionId}"]`,
              );

              if (!wordElement || !definitionElement) return null;

              const wordRect = wordElement.getBoundingClientRect();
              const definitionRect = definitionElement.getBoundingClientRect();
              const svgRect = document
                .querySelector(".vocabulary-connection-svg")
                .getBoundingClientRect();

              const x1 = wordRect.right - svgRect.left;
              const y1 = wordRect.top + wordRect.height / 2 - svgRect.top;
              const x2 = definitionRect.left - svgRect.left;
              const y2 =
                definitionRect.top + definitionRect.height / 2 - svgRect.top;

              return (
                <line
                  key={index}
                  x1={x1}
                  y1={y1}
                  x2={x2}
                  y2={y2}
                  stroke={connection.correct ? "#27ae60" : "#e74c3c"}
                  strokeWidth="3"
                  strokeDasharray={connection.correct ? "0" : "5,5"}
                />
              );
            })}
          </svg>
        </div>

        <div className="vocabulary-definitions-column">
          <h3>Definitions</h3>
          <div className="vocabulary-definitions-list">
            {currentDefinitions.map((definition) => (
              <button
                key={definition.id}
                data-definition-id={definition.id}
                className={`vocabulary-definition-item ${selectedDefinition?.id === definition.id ? "selected" : ""} ${Array.from(matches.values()).includes(definition.id) ? "matched" : ""}`}
                onClick={() => handleDefinitionClick(definition)}
                disabled={Array.from(matches.values()).includes(definition.id)}
              >
                <span className="vocabulary-definition-text">
                  {definition.definition}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="vocabulary-feedback-slot">
        <div className={`vocabulary-feedback-banner ${feedbackMessage ? (feedbackMessage.startsWith("✅") ? "feedback-correct" : "feedback-incorrect") : "feedback-hidden"}`}>
          {feedbackMessage || " "}
        </div>
      </div>
      <div className="wr-card-actions wr-card-actions--grouped">
        <button className="wr-finish-btn" onClick={finishSession}>Complete Session</button>
      </div>
    </div>
  );
};

export default VocabularyMatchingGame;
